import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { TrafficLight } from "@/components/TrafficLight";
import { formatRupiah } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Calculator as CalcIcon, Loader2, Download, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { TrialCounter } from "@/components/TrialCounter";
import { TrialLimitModal } from "@/components/TrialLimitModal";
import { ThreeDayOfferModal } from "@/components/ThreeDayOfferModal";
import { OneAnalysisLeft } from "@/components/OneAnalysisLeft";

const ROUTES = [["datar", "Dominan datar"], ["campuran", "Campuran"], ["tanjakan", "Banyak tanjakan"]];

export default function Calculator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isPremium, checkAuth } = useAuth();
  const [evs, setEvs] = useState([]);
  const [gases, setGases] = useState([]);
  const [evId, setEvId] = useState(params.get("ev") || "");
  const [gasId, setGasId] = useState("");
  const [form, setForm] = useState({
    daily_km: 25, days_per_month: 26, fuel_price_per_liter: 10000,
    electricity_tariff: 1699, public_charging_tariff: 2466, home_charge_pct: 100,
    rider_weight_kg: 70, passenger_cargo_kg: 0, route_condition: "campuran",
  });
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fuels, setFuels] = useState([]);
  const [fuelType, setFuelType] = useState("Pertalite");

  useEffect(() => {
    Promise.all([
      api.get("/vehicles", { params: { category: "motor_listrik" } }),
      api.get("/vehicles", { params: { category: "sepeda_listrik" } }),
      api.get("/vehicles", { params: { category: "motor_bensin" } }),
      api.get("/meta/tariffs"),
    ]).then(([m, b, g, t]) => {
      setEvs([...m.data.items, ...b.data.items]);
      setGases(g.data.items);
      if (!gasId && g.data.items[0]) setGasId(g.data.items[0].vehicle_id);
      const tf = t.data;
      setFuels(tf.fuels || []);
      setForm((f) => ({ ...f, fuel_price_per_liter: tf.fuel?.price_per_liter ?? f.fuel_price_per_liter, electricity_tariff: tf.electricity?.price_per_kwh ?? f.electricity_tariff, public_charging_tariff: tf.public_charging?.price_per_kwh ?? f.public_charging_tariff }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!evId || !gasId) { toast.error("Pilih kendaraan listrik dan motor bensin pembanding."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/analysis/savings", { ev_id: evId, gas_id: gasId, inputs: form });
      setResult(data.result); setMeta(data.meta);
      maybeShow3DayOffer(data.credit);
      setCredit(data.credit);
      await checkAuth();
      toast.success("Hasil berhasil dibuat!");
    } catch (err) {
      handleToolError(err, navigate);
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/analysis/savings/${meta.result_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `hemat-${meta.result_id.slice(0, 8)}.pdf`; a.click();
    } catch { toast.error("Unduh PDF hanya untuk pengguna premium."); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="calculator-page">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-2 flex items-center gap-3"><CalcIcon className="w-8 h-8 text-teal" />Hitung Penghematan</h1>
      <div className="flex items-center gap-3 mb-8 flex-wrap">
        <p className="text-muted-foreground">Bandingkan biaya motor/sepeda listrik dengan motor bensin. Semua nilai dapat Anda ubah.</p>
        <TrialCounter />
      </div>
      <TrialLimitModal />
      <ThreeDayOfferModal />

      <div className="grid lg:grid-cols-5 gap-8">
        {/* FORM */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-2xl p-5 space-y-4">
            <div>
              <Label>Kendaraan Listrik</Label>
              <Combobox testId="calc-ev-select" value={evId} onChange={setEvId} className="mt-1"
                placeholder="Pilih kendaraan listrik" searchPlaceholder="Cari kendaraan…"
                options={evs.map((v) => ({ value: v.vehicle_id, label: `${v.brand} ${v.model}${v.variant ? ` ${v.variant}` : ""}` }))} />
            </div>
            <div>
              <Label>Motor Bensin Pembanding</Label>
              <Combobox testId="calc-gas-select" value={gasId} onChange={setGasId} className="mt-1"
                placeholder="Pilih motor bensin" searchPlaceholder="Cari motor bensin…"
                options={gases.map((v) => ({ value: v.vehicle_id, label: `${v.brand} ${v.model}${v.variant ? ` ${v.variant}` : ""}` }))} />
            </div>
          </Card>
          <Card className="rounded-2xl p-5 grid grid-cols-2 gap-3">
            <NumField form={form} set={set} field="daily_km" label="Jarak per hari" unit="km" />
            <NumField form={form} set={set} field="days_per_month" label="Hari/bulan" unit="hari" />
            <div className="col-span-2">
              <Label className="text-sm">Jenis BBM (motor bensin)</Label>
              <Select value={fuelType} onValueChange={(v) => { setFuelType(v); const fu = fuels.find((x) => x.fuel_type === v); if (fu) set("fuel_price_per_liter", fu.price_per_liter); }}>
                <SelectTrigger data-testid="calc-fuel-type" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{fuels.map((fu) => <SelectItem key={fu.fuel_type} value={fu.fuel_type}>{fu.fuel_type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <NumField form={form} set={set} field="fuel_price_per_liter" label="Harga BBM" unit="/L" />
            <NumField form={form} set={set} field="electricity_tariff" label="Tarif listrik" unit="/kWh" />
            <NumField form={form} set={set} field="home_charge_pct" label="Charge di rumah" unit="%" />
            <NumField form={form} set={set} field="public_charging_tariff" label="Tarif publik" unit="/kWh" />
            <NumField form={form} set={set} field="rider_weight_kg" label="Berat pengendara" unit="kg" />
            <NumField form={form} set={set} field="passenger_cargo_kg" label="Penumpang+barang" unit="kg" />
            <div className="col-span-2">
              <Label className="text-sm">Kondisi rute</Label>
              <Select value={form.route_condition} onValueChange={(v) => set("route_condition", v)}>
                <SelectTrigger data-testid="calc-route" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{ROUTES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>
          <Button data-testid="calc-submit" onClick={submit} disabled={loading} className="w-full h-13 py-6 rounded-full bg-teal hover:bg-teal-hover text-base font-semibold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Hitung Penghematan"}
          </Button>
        </div>

        {/* RESULT */}
        <div className="lg:col-span-3">
          {!result ? (
            <Card className="rounded-2xl p-10 text-center text-muted-foreground h-full flex flex-col items-center justify-center" data-testid="calc-empty">
              <CalcIcon className="w-12 h-12 mb-4 opacity-40" />
              <p>Isi formulir dan tekan <strong>Hitung Penghematan</strong> untuk melihat hasil.</p>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="calc-result">
              <Card className="rounded-2xl p-6" data-testid="calc-summary">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div><p className="text-sm text-muted-foreground">{meta?.ev_name} vs {meta?.gas_name}</p><h2 className="font-heading font-bold text-xl text-navy">Ringkasan</h2></div>
                  <TrafficLight status={result.status} size="lg" />
                </div>
                {result.monthly_savings != null ? (
                  <div className="space-y-5">
                    {(() => { const st = savingsStatus(result); return (
                      <div className={`rounded-xl px-4 py-3 text-sm ${st.cls}`} data-testid="calc-status">
                        <span className="font-bold">{st.label}.</span> {st.desc}
                      </div>
                    ); })()}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">A. Penghematan Operasional</p>
                      <div className="grid grid-cols-2 gap-3">
                        <Box l="Per bulan" v={formatRupiah(result.monthly_savings)} />
                        <Box l="Per tahun" v={formatRupiah(result.yearly_savings)} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Selisih biaya harian (energi, servis, sewa baterai). Belum termasuk harga beli.</p>
                    </div>
                    {result.chart?.length > 5 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">B. Total Biaya Kepemilikan (harga beli + operasional)</p>
                        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="calc-tco">
                          <thead><tr className="text-muted-foreground text-left"><th className="py-1 font-medium">Periode</th><th className="py-1 font-medium">Listrik</th><th className="py-1 font-medium">Bensin</th></tr></thead>
                          <tbody>
                            {[1, 3, 5].map((y) => { const c = result.chart[y]; return c ? (<tr key={y} className="border-t"><td className="py-1">{y} tahun</td><td className="py-1 text-teal font-semibold">{formatRupiah(c.listrik)}</td><td className="py-1">{formatRupiah(c.bensin)}</td></tr>) : null; })}
                          </tbody>
                        </table></div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">C. Balik Modal (Break-even)</p>
                      {result.breakeven_months ? (
                        <p className="text-navy text-sm">Selisih harga beli baru tertutup sekitar bulan ke-<strong>{Math.round(result.breakeven_months)}</strong> (≈ {monthsToText(result.breakeven_months)}).</p>
                      ) : <p className="text-amber-700 text-sm">{result.breakeven_note || "Belum dapat dihitung dari data ini."}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-sm text-muted-foreground" data-testid="calc-insufficient">Data belum cukup untuk menghitung penghematan. {result.missing_data?.join("; ")}</div>
                )}
              </Card>

              {result.chart?.length > 0 && (
                <Card className="rounded-2xl p-6">
                  <h3 className="font-heading font-bold text-navy mb-4">Biaya Kumulatif (5 tahun)</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={result.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="tahun" tickFormatter={(t) => `Thn ${t}`} fontSize={12} />
                      <YAxis tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} fontSize={12} width={40} />
                      <RTooltip formatter={(v) => formatRupiah(v)} labelFormatter={(t) => `Tahun ke-${t}`} />
                      <Legend />
                      <Line type="monotone" dataKey="listrik" name="Motor Listrik" stroke="#00BFA5" strokeWidth={3} dot={false} />
                      <Line type="monotone" dataKey="bensin" name="Motor Bensin" stroke="#EF4444" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <Card className="rounded-2xl p-5">
                  <h3 className="font-heading font-bold text-navy mb-3">Rincian Biaya / bulan</h3>
                  <div className="space-y-2 text-sm">
                    <Row l="Energi listrik" v={formatRupiah(result.ev.energy_cost_month)} />
                    <Row l="Sewa baterai" v={formatRupiah(result.ev.battery_rental_month)} />
                    <Row l="Servis listrik" v={formatRupiah(result.ev.service_month)} />
                    <Row l="Operasional listrik" v={formatRupiah(result.ev.operational_month)} bold />
                    <div className="h-px bg-border my-1" />
                    <Row l="BBM bensin" v={formatRupiah(result.gas.fuel_cost_month)} />
                    <Row l="Operasional bensin" v={formatRupiah(result.gas.operational_month)} bold />
                  </div>
                </Card>
                <Card className="rounded-2xl p-5">
                  <h3 className="font-heading font-bold text-navy mb-3">Skenario Penghematan/bulan</h3>
                  <div className="space-y-2 text-sm">
                    <Row l="Konservatif" v={formatRupiah(result.scenarios.konservatif)} />
                    <Row l="Moderat" v={formatRupiah(result.scenarios.moderat)} bold />
                    <Row l="Optimistis" v={formatRupiah(result.scenarios.optimistis)} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Biaya/km listrik: {formatRupiah(result.ev.cost_per_km)} · bensin: {formatRupiah(result.gas.cost_per_km)}</p>
                </Card>
              </div>

              <Card className="rounded-2xl p-5 bg-slate-50">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-semibold text-navy mb-1">Asumsi & Sumber</p>
                    <p>Faktor rute: {result.assumptions.faktor_rute}× · Efisiensi pengisian: {(result.assumptions.efisiensi_pengisian * 100).toFixed(0)}% · Charge rumah: {result.assumptions.persen_charge_rumah}%</p>
                    {result.notes?.map((n, i) => <p key={i}>• {n}</p>)}
                    <p className="mt-2">Sumber data kendaraan: {meta?.source?.source_name} · Tingkat keyakinan: <strong>{result.confidence}</strong></p>
                    <p className="mt-1 text-xs">Hasil merupakan estimasi, bukan jaminan performa atau penghematan. {meta?.data_status === "demo" && "Berdasarkan DATA DEMO."}</p>
                  </div>
                </div>
              </Card>

              {credit?.remaining === 1 && <div className="mb-3"><OneAnalysisLeft to={`/tanjakan${evId ? `?vehicle=${evId}` : ""}`} label="Selanjutnya: Cek Tanjakan" /></div>}
              <div className="flex flex-wrap gap-2">
                <Button data-testid="calc-reset" variant="outline" onClick={() => setResult(null)} className="rounded-full"><RotateCcw className="w-4 h-4 mr-2" />Ubah Asumsi</Button>
                <Button data-testid="calc-pdf" onClick={downloadPdf} className="rounded-full bg-navy hover:bg-navy/90"><Download className="w-4 h-4 mr-2" />Unduh PDF {!isPremium() && "(Premium)"}</Button>
                <Button data-testid="calc-next-hill" variant="outline" onClick={() => navigate(`/tanjakan${evId ? `?vehicle=${evId}` : ""}`)} className="rounded-full">Selanjutnya: Cek Tanjakan</Button>
                <Button data-testid="calc-compare" variant="outline" onClick={() => navigate("/bandingkan")} className="rounded-full">Bandingkan Kendaraan Lain</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={bold ? "font-bold text-navy" : "text-navy"}>{v}</span></div>;
}

function Box({ l, v }) {
  return <div className="bg-accent rounded-xl p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-heading font-extrabold text-teal text-lg">{v}</p></div>;
}

function monthsToText(m) {
  const t = Math.round(m); const y = Math.floor(t / 12); const mo = t % 12;
  return (y ? `${y} tahun ` : "") + (mo ? `${mo} bulan` : (y ? "" : "0 bulan"));
}

function savingsStatus(r) {
  if (r.monthly_savings == null) return { label: "Data belum cukup", desc: "Sebagian input belum lengkap.", cls: "bg-slate-100 text-slate-700" };
  if (r.monthly_savings <= 0) return { label: "Harga awal lebih tinggi", desc: "Pada asumsi ini biaya operasional listrik belum lebih murah.", cls: "bg-red-50 text-red-700" };
  if (r.breakeven_months && r.breakeven_months > 60) return { label: "Belum balik modal dalam 5 tahun", desc: "Hemat operasional, tetapi selisih harga beli baru tertutup setelah 5 tahun.", cls: "bg-amber-50 text-amber-800" };
  return { label: "Hemat operasional", desc: "Biaya harian lebih murah dan berpotensi balik modal dalam masa pakai.", cls: "bg-teal/10 text-teal" };
}

function NumField({ field, label, unit, form, set }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="relative">
        <Input data-testid={`calc-${field}`} type="number" value={form[field]} onChange={(e) => set(field, Number(e.target.value))} className="rounded-xl h-11 mt-1" />
        {unit && <span className="absolute right-3 top-1/2 translate-y-[1px] text-xs text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
}
